import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../Layout';
import aoAPI from '../../api/aoAPI';
import ClientSearchSelect from './ClientSearchSelect';
import {
  PencilIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const MONTHS = [
  { value: 1,  label: 'January'   }, { value: 2,  label: 'February'  },
  { value: 3,  label: 'March'     }, { value: 4,  label: 'April'     },
  { value: 5,  label: 'May'       }, { value: 6,  label: 'June'      },
  { value: 7,  label: 'July'      }, { value: 8,  label: 'August'    },
  { value: 9,  label: 'September' }, { value: 10, label: 'October'   },
  { value: 11, label: 'November'  }, { value: 12, label: 'December'  },
];

const QUARTERS = [
  { value: 1, label: 'Q1 (Jan–Mar)', endMonth: 3 },
  { value: 2, label: 'Q2 (Apr–Jun)', endMonth: 6 },
  { value: 3, label: 'Q3 (Jul–Sep)', endMonth: 9 },
  { value: 4, label: 'Q4 (Oct–Dec)', endMonth: 12 },
];

const FREQUENCY_TABS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

function buildQuarterlyMonths(startQuarter) {
  const ends = { 1: 3, 2: 6, 3: 9, 4: 12 };
  const months = [];
  for (let q = startQuarter; q <= 4; q += 1) months.push(ends[q]);
  return months;
}

const fmt = (n) =>
  n ? new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n) : '—';

const monthLabel = (m) => MONTHS.find(x => x.value === m)?.label || m;

const FreqBadge = ({ freq }) => {
  if (freq === 'monthly')
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">Monthly</span>;
  if (freq === 'quarterly')
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">Quarterly</span>;
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">None</span>;
};

const AORecurringScreen = () => {
  const [clients,    setClients]    = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  // inline-edit row state
  const [editRow,    setEditRow]    = useState(null); // clientId currently open
  const [editForm,   setEditForm]   = useState({});
  const [savingRow,  setSavingRow]  = useState(null);
  const [rowError,   setRowError]   = useState('');

  // generate / preview section
  const [billingFrequency, setBillingFrequency] = useState('monthly');
  const [startMonth, setStartMonth] = useState(new Date().getMonth() + 1);
  const [startQuarter, setStartQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [previewing,    setPreviewing]    = useState(false);
  const [previewData,   setPreviewData]   = useState(null); // { fiscalYear, frequency, months, preview: [...] }
  const [previewParams, setPreviewParams] = useState(null);
  const [previewError,  setPreviewError]  = useState('');

  // confirmation modal + generate
  const [showModal,  setShowModal]  = useState(false);
  const [generating, setGenerating] = useState(false);

  // result summary after generate
  const [result, setResult] = useState(null);

  // ── Load clients ──────────────────────────────────────────────────────────
  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await aoAPI.getClients({ isActive: true });
      setClients(res.data || []);
    } catch (e) {
      setError(typeof e === 'string' ? e : 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  // ── Inline-edit helpers ───────────────────────────────────────────────────
  const startEdit = (client) => {
    setEditRow(client._id);
    setEditForm({
      pfFrequency:  client.pfFrequency  || 'none',
      pfAmount:     client.pfAmount     || '',
      pfStartMonth: client.pfStartMonth || 1,
    });
    setRowError('');
  };

  const cancelEdit = () => {
    setEditRow(null);
    setEditForm({});
    setRowError('');
  };

  const saveEdit = async (clientId) => {
    const amount = parseFloat(editForm.pfAmount);
    if (editForm.pfFrequency !== 'none' && (!editForm.pfAmount || isNaN(amount) || amount <= 0)) {
      setRowError('Enter a valid PF amount');
      return;
    }
    try {
      setSavingRow(clientId);
      setRowError('');
      await aoAPI.updateClientRecurring(clientId, {
        pfFrequency:  editForm.pfFrequency,
        pfAmount:     editForm.pfFrequency === 'none' ? 0 : amount,
        pfStartMonth: parseInt(editForm.pfStartMonth, 10),
      });
      await loadClients();
      setEditRow(null);
    } catch (e) {
      setRowError(typeof e === 'string' ? e : 'Save failed');
    } finally {
      setSavingRow(null);
    }
  };

  // ── Preview ───────────────────────────────────────────────────────────────
  const handlePreview = async () => {
    try {
      setPreviewing(true);
      setPreviewData(null);
      setPreviewError('');
      setResult(null);
      const params = {
        fiscalYear,
        frequency: billingFrequency,
        ...(billingFrequency === 'monthly' ? { startMonth } : { startQuarter }),
      };
      const res = await aoAPI.getRecurringPreview(params);
      setPreviewData(res.data || res);
      setPreviewParams(params);
    } catch (e) {
      setPreviewError(typeof e === 'string' ? e : 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  // ── Generate ──────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await aoAPI.generateRecurring({
        fiscalYear,
        frequency: billingFrequency,
        ...(billingFrequency === 'monthly' ? { startMonth } : { startQuarter }),
      });
      setShowModal(false);
      setResult(res.data || res);
      setPreviewData(null);
      setPreviewParams(null);
    } catch (e) {
      setShowModal(false);
      setPreviewError(typeof e === 'string' ? e : 'Generate failed');
    } finally {
      setGenerating(false);
    }
  };

  const newToInsert = previewData?.preview?.filter(p => !p.alreadyExists).length ?? 0;
  const alreadyExisting = previewData?.preview?.filter(p => p.alreadyExists).length ?? 0;
  const visibleClients = selectedClient
    ? clients.filter(c => String(c._id) === String(selectedClient))
    : clients;

  const groupedPreview = (previewData?.preview || []).reduce((acc, row) => {
    if (!acc[row.clientName]) acc[row.clientName] = [];
    acc[row.clientName].push(row);
    return acc;
  }, {});

  const frequencyLabel = billingFrequency === 'monthly' ? 'monthly' : 'quarterly';
  const startLabel = billingFrequency === 'monthly'
    ? `${monthLabel(startMonth)}`
    : `Q${startQuarter}`;
  const currentParamsKey = JSON.stringify({
    fiscalYear,
    frequency: billingFrequency,
    ...(billingFrequency === 'monthly' ? { startMonth } : { startQuarter }),
  });
  const previewParamsKey = previewParams ? JSON.stringify(previewParams) : null;
  const isPreviewFresh = Boolean(previewData) && currentParamsKey === previewParamsKey;

  return (
    <Layout pageTitle="Recurring PF Maintenance">
      <div className="p-6 max-w-5xl mx-auto">

        {/* Page header */}
        <div className="mb-5">
          <h2 className="text-xl font-bold text-gray-800">Recurring PF Maintenance</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Configure and generate recurring Professional Fee transactions per client.
          </p>
        </div>

        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}

        <div className="mb-4 max-w-md">
          <label className="block text-xs font-medium text-gray-600 mb-1">Client Filter</label>
          <ClientSearchSelect
            clients={clients}
            value={selectedClient}
            onChange={(id) => setSelectedClient(id)}
            placeholder="— Search client —"
          />
        </div>

        {/* ── Client configuration table ─────────────────────────────────── */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Client PF Configuration</h3>

          {loading ? (
            <p className="text-sm text-gray-400">Loading clients…</p>
          ) : visibleClients.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No active clients found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 text-xs text-gray-500">
                  <tr>
                    <th className="pb-2 text-left pl-1">Client</th>
                    <th className="pb-2 text-left">In-charge</th>
                    <th className="pb-2 text-center">PF Frequency</th>
                    <th className="pb-2 text-right pr-3">PF Amount</th>
                    <th className="pb-2 text-center">Start Month</th>
                    <th className="pb-2 text-center w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleClients.map(c => (
                    <tr key={c._id} className="hover:bg-white">
                      <td className="py-2.5 pl-1">
                        <p className="font-medium text-gray-800">{c.name}</p>
                        {c.code && <p className="text-xs text-gray-400">{c.code}</p>}
                      </td>
                      <td className="py-2.5 text-gray-500 text-sm">{c.inCharge?.name || '—'}</td>

                      {editRow === c._id ? (
                        <>
                          {/* ── Inline edit cells ── */}
                          <td className="py-2 text-center">
                            <select
                              value={editForm.pfFrequency}
                              onChange={e => setEditForm(f => ({ ...f, pfFrequency: e.target.value }))}
                              className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500"
                            >
                              <option value="none">None</option>
                              <option value="monthly">Monthly</option>
                              <option value="quarterly">Quarterly</option>
                            </select>
                          </td>
                          <td className="py-2 text-right pr-3">
                            <input
                              type="number"
                              value={editForm.pfAmount}
                              onChange={e => setEditForm(f => ({ ...f, pfAmount: e.target.value }))}
                              placeholder="0.00"
                              min={0}
                              step={0.01}
                              className="w-24 border border-gray-300 rounded-md px-2 py-1 text-xs text-right focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                          </td>
                          <td className="py-2 text-center">
                            <select
                              value={editForm.pfStartMonth}
                              onChange={e => setEditForm(f => ({ ...f, pfStartMonth: e.target.value }))}
                              disabled={editForm.pfFrequency !== 'quarterly'}
                              className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-40"
                            >
                              {MONTHS.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-2 text-center">
                            <div className="flex flex-col items-center gap-1">
                              {rowError && (
                                <span className="text-xs text-red-500">{rowError}</span>
                              )}
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => saveEdit(c._id)}
                                  disabled={savingRow === c._id}
                                  className="bg-teal-600 hover:bg-teal-700 text-white text-xs px-2.5 py-1 rounded-md disabled:opacity-60 transition-colors"
                                >
                                  {savingRow === c._id ? '…' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="border border-gray-300 text-gray-500 hover:bg-gray-100 text-xs px-2.5 py-1 rounded-md transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          {/* ── Read-only cells ── */}
                          <td className="py-2.5 text-center">
                            <FreqBadge freq={c.pfFrequency} />
                          </td>
                          <td className="py-2.5 text-right pr-3 font-mono text-gray-700">
                            {c.pfAmount ? `₱${fmt(c.pfAmount)}` : '—'}
                          </td>
                          <td className="py-2.5 text-center text-xs text-gray-500">
                            {c.pfFrequency === 'quarterly' && c.pfStartMonth
                              ? monthLabel(c.pfStartMonth)
                              : '—'}
                          </td>
                          <td className="py-2.5 text-center">
                            <button
                              onClick={() => startEdit(c)}
                              className="text-gray-400 hover:text-teal-600"
                              title="Edit PF settings"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Generate section ───────────────────────────────────────────── */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Generate Recurring PF</h3>

          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5 mb-4">
            {FREQUENCY_TABS.map(tab => (
              <button
                key={tab.value}
                onClick={() => {
                  const currentMonth = new Date().getMonth() + 1;
                  setBillingFrequency(tab.value);
                  setStartMonth(currentMonth);
                  setStartQuarter(Math.ceil(currentMonth / 3));
                  setPreviewData(null);
                  setPreviewParams(null);
                  setResult(null);
                }}
                className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                  billingFrequency === tab.value
                    ? 'bg-white text-teal-700 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-end gap-3 mb-5">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Fiscal Year</label>
              <input
                type="number"
                value={fiscalYear}
                onChange={e => {
                  setFiscalYear(parseInt(e.target.value, 10));
                  setPreviewData(null);
                  setPreviewParams(null);
                  setResult(null);
                }}
                min={2000}
                max={2100}
                className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>

            {billingFrequency === 'monthly' ? (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Month</label>
                <select
                  value={startMonth}
                  onChange={e => {
                    setStartMonth(parseInt(e.target.value, 10));
                    setPreviewData(null);
                    setPreviewParams(null);
                    setResult(null);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Will generate months {startMonth}–12 ({12 - startMonth + 1} transaction{12 - startMonth + 1 !== 1 ? 's' : ''} per client)
                </p>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Start Quarter</label>
                <select
                  value={startQuarter}
                  onChange={e => {
                    setStartQuarter(parseInt(e.target.value, 10));
                    setPreviewData(null);
                    setPreviewParams(null);
                    setResult(null);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {QUARTERS.map(q => (
                    <option key={q.value} value={q.value}>{q.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Will generate Q{startQuarter}–Q4 · months:{' '}
                  {buildQuarterlyMonths(startQuarter)
                    .map(m => MONTHS.find(x => x.value === m)?.label)
                    .join(', ')}
                </p>
              </div>
            )}

            <button
              onClick={handlePreview}
              disabled={previewing}
              className="border border-teal-600 text-teal-600 hover:bg-teal-50 text-sm px-5 py-2 rounded-lg disabled:opacity-60 transition-colors"
            >
              {previewing ? 'Loading…' : 'Preview'}
            </button>
            {previewData && (
              <button
                onClick={() => { if (isPreviewFresh) setShowModal(true); }}
                disabled={!isPreviewFresh}
                className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-5 py-2 rounded-lg transition-colors disabled:opacity-60"
              >
                Generate
              </button>
            )}
          </div>

          {previewData && !isPreviewFresh && (
            <p className="text-xs text-amber-600 mb-3">
              Preview is outdated for the current settings. Click Preview again before generating.
            </p>
          )}

          {previewError && <p className="text-sm text-red-500 mb-3">{previewError}</p>}

          {/* Preview table */}
          {previewData?.preview?.length > 0 && (
            <div className="overflow-x-auto">
              <p className="text-xs text-gray-500 mb-2">
                Preview for <strong>{billingFrequency === 'monthly' ? `months ${startMonth}–12` : `Q${startQuarter}–Q4`}</strong>{' '}
                in FY <strong>{fiscalYear}</strong> — {previewData.preview.length} row(s)
              </p>
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100 text-xs text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Client</th>
                    <th className="px-4 py-2 text-center">Frequency</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2 text-center">Month</th>
                    <th className="px-4 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {Object.entries(groupedPreview).map(([clientName, rows]) => (
                    <React.Fragment key={clientName}>
                      <tr className="bg-gray-50">
                        <td colSpan={5} className="px-4 py-2 text-xs font-semibold text-gray-600">{clientName}</td>
                      </tr>
                      {rows.map((p, i) => (
                        <tr key={`${clientName}-${p.month}-${i}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-800 font-medium">{p.clientName}</td>
                          <td className="px-4 py-2 text-center"><FreqBadge freq={p.pfFrequency} /></td>
                          <td className="px-4 py-2 text-right font-mono text-gray-700">₱{fmt(p.pfAmount)}</td>
                          <td className="px-4 py-2 text-center text-gray-700">{monthLabel(p.month)}</td>
                          <td className="px-4 py-2 text-center">
                            {p.alreadyExists ? (
                              <span className="text-gray-400 text-xs">⏭ Already exists</span>
                            ) : (
                              <span className="text-teal-600 text-xs font-medium">✅ Will insert</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {previewData?.preview?.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No clients configured for this frequency / fiscal year range.
            </p>
          )}
        </div>

        {/* ── Result summary ─────────────────────────────────────────────── */}
        {result && (
          <div className="mb-6">
            <div className="rounded-xl border border-green-200 bg-green-50 px-5 py-4 mb-4">
              <p className="text-sm font-semibold text-green-700 flex items-center gap-2">
                <CheckIcon className="h-5 w-5 text-green-600 shrink-0" />
                Generation complete — {result.inserted} inserted · {result.skipped} skipped
              </p>
              <p className="text-xs text-green-600 mt-0.5">
                {billingFrequency === 'monthly'
                  ? `Months ${startMonth}–12, FY ${fiscalYear}`
                  : `Q${startQuarter}–Q4 (${buildQuarterlyMonths(startQuarter).map(m => MONTHS.find(x => x.value === m)?.label).join(', ')}), FY ${fiscalYear}`
                }
              </p>
            </div>
            {result.results?.length > 0 && (
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100 text-xs text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Client</th>
                    <th className="px-4 py-2 text-center">Month</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {result.results.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-800 font-medium">{r.clientName}</td>
                      <td className="px-4 py-2 text-center text-gray-700">{monthLabel(r.month)}</td>
                      <td className="px-4 py-2 text-right font-mono text-gray-700">₱{fmt(r.amount)}</td>
                      <td className="px-4 py-2 text-center">
                        {r.status === 'inserted' ? (
                          <span className="text-teal-600 text-xs font-medium">✅ Inserted</span>
                        ) : (
                          <span className="text-gray-400 text-xs">⏭ Skipped</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── Confirmation modal ──────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg">
            <h3 className="text-base font-semibold text-gray-800 mb-2">Generate Recurring PF?</h3>
            <p className="text-sm text-gray-600 mb-2">
              This will insert <strong>{newToInsert}</strong> transaction{newToInsert !== 1 ? 's' : ''} for <strong>{frequencyLabel}</strong> clients
              {' '}from <strong>{startLabel}</strong> to December <strong>{fiscalYear}</strong>.
            </p>
            <p className="text-sm text-gray-600 mb-2">
              <strong>{alreadyExisting}</strong> already exist and will be skipped.
            </p>
            <p className="text-xs text-gray-400 mb-5">
              This operation is safe to run multiple times — existing transactions are skipped automatically.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="border border-gray-300 text-gray-600 hover:bg-gray-100 text-sm px-4 py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-5 py-2 rounded-lg disabled:opacity-60 transition-colors"
              >
                {generating ? 'Generating…' : `Generate ${newToInsert} Transactions`}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AORecurringScreen;
